import { onBeforeUnmount, ref, watch, type WatchSource } from 'vue'
import { publicApi, type PublicStatus } from '@/api/public'
import { publicStatusFreshnessToken } from './publicFreshnessToken'

const pollIntervalMs = 30_000
const autoRefreshMinDelayMs = 250
const autoRefreshMaxDelayMs = 2_500
const autoRefreshRetryDelayMs = 30_000

const latestPublicDataToken = ref<string | null>(null)
const lastSeenPublicDataToken = ref<string | null>(null)
const staleSince = ref<string | null>(null)

let started = false
let pollTimer: number | undefined
let inFlight: Promise<void> | null = null

export function startPublicStatusPolling(): void {
  if (started) return
  started = true
  document.addEventListener('visibilitychange', handleVisibilityChange)
  void checkPublicStatus()
  pollTimer = window.setInterval(() => {
    if (!document.hidden) {
      void checkPublicStatus()
    }
  }, pollIntervalMs)
}

export function stopPublicStatusPolling(): void {
  if (!started) return
  started = false
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (pollTimer !== undefined) {
    window.clearInterval(pollTimer)
    pollTimer = undefined
  }
}

export function markPublicDataSeen(): void {
  if (!latestPublicDataToken.value) {
    staleSince.value = null
    return
  }
  lastSeenPublicDataToken.value = latestPublicDataToken.value
  staleSince.value = null
}

export function usePublicFreshnessAutoRefresh(
  refresh: () => Promise<void> | void,
  busySource: WatchSource<boolean> = () => false,
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  let pending = false
  let running = false
  let stopped = false
  let refreshTimer: number | undefined

  const clearRefreshTimer = () => {
    if (refreshTimer === undefined) return
    window.clearTimeout(refreshTimer)
    refreshTimer = undefined
  }

  const scheduleRefresh = (
    delayMs = publicFreshnessAutoRefreshDelayMs(),
    scheduleWhileBlocked = false,
  ) => {
    if (stopped) return
    if (!staleSince.value) {
      pending = false
      clearRefreshTimer()
      return
    }
    if (
      !scheduleWhileBlocked &&
      (document.hidden || running || readWatchSource(busySource))
    ) {
      pending = true
      return
    }
    if (refreshTimer !== undefined) return

    refreshTimer = window.setTimeout(() => {
      refreshTimer = undefined
      if (stopped) return
      if (!staleSince.value) {
        pending = false
        return
      }
      if (document.hidden || running || readWatchSource(busySource)) {
        pending = true
        return
      }

      pending = false
      running = true
      void Promise.resolve()
        .then(refresh)
        .catch(() => undefined)
        .finally(() => {
          running = false
          if (stopped || !staleSince.value) return

          // A failed/bypassed refresh leaves the revision stale. Retry at a
          // bounded cadence instead of depending on another status-token
          // change (or immediately spinning). The timer may count down while
          // hidden/busy, but its callback never starts work in those states.
          pending = false
          scheduleRefresh(autoRefreshRetryDelayMs, true)
        })
    }, delayMs)
  }

  const stopFreshnessWatch = watch(staleSince, next => {
    if (next) {
      scheduleRefresh()
      return
    }
    pending = false
    clearRefreshTimer()
  }, { immediate: true, flush: 'post' })

  const stopBusyWatch = watch(busySource, isBusy => {
    if (!isBusy && pending) {
      pending = false
      scheduleRefresh()
    }
  }, { flush: 'post' })

  const handleAutoRefreshVisibilityChange = () => {
    if (!document.hidden && staleSince.value) {
      pending = false
      scheduleRefresh()
    }
  }

  document.addEventListener('visibilitychange', handleAutoRefreshVisibilityChange)
  onBeforeUnmount(() => {
    stopped = true
    pending = false
    stopFreshnessWatch()
    stopBusyWatch()
    clearRefreshTimer()
    document.removeEventListener('visibilitychange', handleAutoRefreshVisibilityChange)
  })
}

function handleVisibilityChange(): void {
  if (!document.hidden) {
    void checkPublicStatus()
  }
}

function checkPublicStatus(): Promise<void> {
  if (inFlight) return inFlight
  inFlight = doCheckPublicStatus().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function doCheckPublicStatus(): Promise<void> {
  try {
    applyStatus(await publicApi.status())
  } catch {
    // Background freshness polling is best-effort; the next interval retries.
  }
}

function applyStatus(status: PublicStatus): void {
  const nextMatchID = status.latest_match_id ?? null
  const nextCapturedAt = status.latest_match_captured_at ?? null
  const nextToken = publicStatusFreshnessToken(status)
  latestPublicDataToken.value = nextToken
  if (!lastSeenPublicDataToken.value) {
    lastSeenPublicDataToken.value = nextToken
    return
  }
  if (lastSeenPublicDataToken.value !== nextToken) {
    staleSince.value = nextCapturedAt ?? nextMatchID ?? `revision:${nextToken}`
  }
}

function readWatchSource(source: WatchSource<boolean>): boolean {
  if (typeof source === 'function') {
    return source()
  }
  return source.value
}

function publicFreshnessAutoRefreshDelayMs(): number {
  return autoRefreshMinDelayMs + Math.floor(Math.random() * (autoRefreshMaxDelayMs - autoRefreshMinDelayMs + 1))
}
